/**
 * /design-system/operational/hk-os · HK-OS Gallery
 * ────────────────────────────────────────────────────────────────────
 * In-app Storybook-equivalent for the Housekeeping Voice-First primitives.
 * Every primitive is exercised in each canonical state (Default, Loading,
 * Offline, Disabled, Voice Playing, Voice Cached, Emergency, VIP, Success,
 * Error). No workflows — just visual regression + design surface.
 *
 * The whole page is wrapped in <VoiceRegistryProvider> so playback works
 * against the live OA endpoints.
 */
import React, { useState } from "react";
import {
  Camera, MicIcon, Sparkles, Volume2, QrCode, CheckCircle2, WifiOff,
  ClipboardList, Crown, AlertTriangle, PartyPopper, Play, Trash2,
} from "lucide-react";
import {
  VoiceRegistryProvider,
  VoicePlayer,
  VoiceInstructionBar,
  TaskProgressCard,
  NextTaskCard,
  ScanRoomButton,
  VoiceRecorderButton,
  PhotoEvidenceUploader,
  OfflineSyncIndicator,
  VoiceWarmupManager,
  AttentionOverlay,
  TaskCompletionButton,
} from "@/components/operational/hk_os";

function Section({ id, title, blurb, children }) {
  return (
    <section
      id={id}
      className="mb-12 scroll-mt-24"
      data-testid={`hk-os-section-${id}`}
    >
      <div className="mb-4">
        <h2 className="text-[22px] font-bold text-slate-900">{title}</h2>
        {blurb && (
          <p className="mt-1 text-[14px] text-slate-600 max-w-3xl">{blurb}</p>
        )}
      </div>
      <div className="grid gap-4">{children}</div>
    </section>
  );
}

function Tile({ label, children, testid }) {
  return (
    <div
      className="rounded-2xl border-2 border-slate-200 bg-white p-5 space-y-3"
      data-testid={testid}
    >
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="flex items-center flex-wrap gap-3">{children}</div>
    </div>
  );
}

// Auth token bridge — HK-OS gallery reuses the current user's JWT so the
// live OA endpoints answer. Falls back to null (provider still renders).
function useLocalAuthToken() {
  try {
    return window.localStorage.getItem("hocp.jwt") || null;
  } catch {
    return null;
  }
}

function GalleryBody() {
  const [attentionVariant, setAttentionVariant] = useState(null);

  const openAttention = (v) => setAttentionVariant(v);
  const closeAttention = () => setAttentionVariant(null);

  const nav = [
    ["voice-warmup",   "Voice Warm-up Manager"],
    ["voice-player",   "Voice Player"],
    ["voice-bar",      "Voice Instruction Bar"],
    ["scan-room",      "Scan Room Button"],
    ["next-task",      "Next Task Card"],
    ["task-progress",  "Task Progress Card"],
    ["voice-recorder", "Voice Recorder Button"],
    ["photo-upload",   "Photo Evidence Uploader"],
    ["offline-sync",   "Offline Sync Indicator"],
    ["completion",     "Task Completion Button"],
    ["attention",      "Attention Overlay"],
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600">
              HK-OS · Phase 4
            </div>
            <h1 className="text-[26px] font-black text-slate-900 leading-tight">
              Housekeeping Voice-First Primitives
            </h1>
            <p className="text-[13px] text-slate-500 max-w-2xl">
              Reusable operational building blocks. No business logic; no
              workflows. Bengali text arrives from the Operational Assistant
              at runtime.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <OfflineSyncIndicator />
            <VoiceWarmupManager hideWhenReady />
          </div>
        </div>
        <nav className="max-w-7xl mx-auto mt-4 flex flex-wrap gap-2 text-[12px]">
          {nav.map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className="rounded-full border border-slate-200 px-3 py-1 hover:border-sky-400 hover:text-sky-700"
            >
              {label}
            </a>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* 1 · Voice Warm-up */}
        <Section id="voice-warmup" title="Voice Warm-up Manager"
                  blurb="Runs automatically on first launch. Downloads the manifest, populates the Cache API with every audio asset, tracks manifest version.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Tile label="Compact (ready)" testid="tile-warmup-ready">
              <VoiceWarmupManager hideWhenReady={false} />
            </Tile>
            <Tile label="With refresh action" testid="tile-warmup-refresh">
              <VoiceWarmupManager hideWhenReady={false} />
            </Tile>
          </div>
        </Section>

        {/* 2 · VoicePlayer */}
        <Section id="voice-player" title="Voice Player"
                  blurb="Universal snippet playback. Cache-first, progress ring, offline badge.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Tile label="Default (VOICE_WELCOME)" testid="tile-player-default">
              <VoicePlayer voiceKey="VOICE_WELCOME" testid="player-welcome" />
            </Tile>
            <Tile label="Small · no text" testid="tile-player-small">
              <VoicePlayer voiceKey="VOICE_NEW_TASK" size="sm" showText={false}
                             testid="player-sm" />
            </Tile>
            <Tile label="Large" testid="tile-player-large">
              <VoicePlayer voiceKey="VOICE_ROOM_READY" size="lg"
                             testid="player-lg" />
            </Tile>
            <Tile label="Unknown key (disabled)" testid="tile-player-disabled">
              <VoicePlayer voiceKey="VOICE_UNKNOWN_KEY_XYZ"
                             testid="player-disabled" />
            </Tile>
            <Tile label="Auto-play once" testid="tile-player-autoplay">
              <VoicePlayer voiceKey="VOICE_GOOD_JOB" autoPlay
                             testid="player-autoplay" />
            </Tile>
            <Tile label="Error snippet (VOICE_ERROR)" testid="tile-player-error">
              <VoicePlayer voiceKey="VOICE_ERROR" testid="player-error" />
            </Tile>
          </div>
        </Section>

        {/* 3 · VoiceInstructionBar */}
        <Section id="voice-bar" title="Voice Instruction Bar"
                  blurb="Always-visible sticky bar with icon, Bengali text, replay button and progress rail.">
          <Tile label="Default" testid="tile-bar-default">
            <div className="w-full">
              <VoiceInstructionBar voiceKey="VOICE_SCAN_ROOM"
                progress={0.35} taskIcon={QrCode}
                testid="bar-scan" autoPlay={false} />
            </div>
          </Tile>
          <Tile label="Hero variant · task cleaning" testid="tile-bar-hero">
            <div className="w-full">
              <VoiceInstructionBar voiceKey="VOICE_ROOM_DIRTY"
                progress={0.62} taskIcon={Sparkles} variant="hero"
                testid="bar-hero" autoPlay={false} />
            </div>
          </Tile>
          <Tile label="Compact · almost done" testid="tile-bar-compact">
            <div className="w-full">
              <VoiceInstructionBar voiceKey="VOICE_TASK_COMPLETED"
                progress={0.95} taskIcon={CheckCircle2} variant="compact"
                testid="bar-compact" autoPlay={false} />
            </div>
          </Tile>
        </Section>

        {/* 4 · ScanRoomButton */}
        <Section id="scan-room" title="Scan Room Button"
                  blurb="Large tap target · launches the camera and plays voice.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Tile label="Default" testid="tile-scan-default">
              <ScanRoomButton onLaunch={() => alert("(demo) camera")}
                                testid="scan-default" />
            </Tile>
            <Tile label="Disabled" testid="tile-scan-disabled">
              <ScanRoomButton disabled testid="scan-disabled" />
            </Tile>
          </div>
        </Section>

        {/* 5 · NextTaskCard */}
        <Section id="next-task" title="Next Task Card"
                  blurb="The single source of truth for 'what to do next'. No room lists, ever.">
          <div className="grid lg:grid-cols-2 gap-4">
            <Tile label="Default · Departure Cleaning · VIP" testid="tile-next-default">
              <div className="w-full">
                <NextTaskCard
                  roomNumber="305" floorLabel="Floor 3 · Wing B"
                  taskTypeKey="VOICE_ROOM_DIRTY"
                  badges={[{ label: "VIP", tone: "vip" },
                            { label: "Priority", tone: "priority" }]}
                  onStart={() => alert("(demo) start task")}
                  testid="next-default"
                />
              </div>
            </Tile>
            <Tile label="Loading" testid="tile-next-loading">
              <div className="w-full">
                <NextTaskCard loading testid="next-loading" />
              </div>
            </Tile>
            <Tile label="Empty · shift complete" testid="tile-next-empty">
              <div className="w-full">
                <NextTaskCard empty emptyKey="VOICE_SHIFT_ENDED"
                                testid="next-empty" />
              </div>
            </Tile>
            <Tile label="Standard clean · no badges" testid="tile-next-plain">
              <div className="w-full">
                <NextTaskCard roomNumber="204" floorLabel="Floor 2"
                                taskTypeKey="VOICE_ROOM_DIRTY"
                                onStart={() => {}} testid="next-plain" />
              </div>
            </Tile>
          </div>
        </Section>

        {/* 6 · TaskProgressCard */}
        <Section id="task-progress" title="Task Progress Card"
                  blurb="Hero card showing the CURRENT task with primary CTA.">
          <div className="grid lg:grid-cols-2 gap-4">
            <Tile label="In progress · 62%" testid="tile-progress-default">
              <div className="w-full">
                <TaskProgressCard
                  roomNumber="305" floorLabel="Floor 3 · Wing B"
                  taskTypeKey="VOICE_ROOM_DIRTY"
                  badges={[{ label: "VIP", tone: "vip" }]}
                  etaMinutes={12} progress={0.62}
                  primaryCta={{ voiceKey: "VOICE_TASK_COMPLETED",
                                  tone: "success",
                                  icon: CheckCircle2,
                                  onClick: () => alert("(demo)") }}
                  testid="progress-default"
                />
              </div>
            </Tile>
            <Tile label="Priority + Priority badge · early" testid="tile-progress-priority">
              <div className="w-full">
                <TaskProgressCard
                  roomNumber="112" floorLabel="Floor 1"
                  taskTypeKey="VOICE_MAINTENANCE"
                  badges={[{ label: "Priority", tone: "priority" }]}
                  etaMinutes={4} progress={0.15}
                  primaryCta={{ voiceKey: "VOICE_START_TASK",
                                  tone: "primary",
                                  icon: Play,
                                  onClick: () => alert("(demo)") }}
                  testid="progress-priority"
                />
              </div>
            </Tile>
          </div>
        </Section>

        {/* 7 · VoiceRecorderButton */}
        <Section id="voice-recorder" title="Voice Recorder Button"
                  blurb="Hold-to-record UI for defects, damage, L&F, supervisor notes.">
          <div className="grid sm:grid-cols-3 gap-4">
            <Tile label="Default" testid="tile-recorder-default">
              <VoiceRecorderButton onStop={(b) => console.log("blob", b)}
                                     testid="rec-default" />
            </Tile>
            <Tile label="Disabled" testid="tile-recorder-disabled">
              <VoiceRecorderButton disabled testid="rec-disabled" />
            </Tile>
            <Tile label="Defect prompt" testid="tile-recorder-defect">
              <VoiceRecorderButton promptKey="VOICE_DEFECT_FOUND"
                                     testid="rec-defect" />
            </Tile>
          </div>
        </Section>

        {/* 8 · PhotoEvidenceUploader */}
        <Section id="photo-upload" title="Photo Evidence Uploader"
                  blurb="Camera-first. Compression, retake, preview, upload progress.">
          <Tile label="Default" testid="tile-photo-default">
            <div className="w-full">
              <PhotoEvidenceUploader
                onUpload={async ({ onProgress }) => {
                  // Demo — simulate a 1-second upload
                  for (let i = 1; i <= 5; i++) {
                    await new Promise((r) => setTimeout(r, 200));
                    onProgress?.(i / 5);
                  }
                  return { ok: true, media_id: "demo-1" };
                }}
                testid="photo-default"
              />
            </div>
          </Tile>
        </Section>

        {/* 9 · OfflineSyncIndicator */}
        <Section id="offline-sync" title="Offline Sync Indicator"
                  blurb="All five states, reusable across every operational module.">
          <div className="flex flex-wrap gap-3">
            <OfflineSyncIndicator state="online" testid="sync-online" />
            <OfflineSyncIndicator state="syncing" testid="sync-syncing" />
            <OfflineSyncIndicator state="offline" queuedCount={4}
                                    testid="sync-offline" />
            <OfflineSyncIndicator state="queued" queuedCount={2}
                                    testid="sync-queued" />
            <OfflineSyncIndicator state="failed"
                                    onRetry={() => {}}
                                    testid="sync-failed" />
            <OfflineSyncIndicator state="ok" testid="sync-ok" />
            <OfflineSyncIndicator state="online" dense testid="sync-online-dense" />
          </div>
        </Section>

        {/* 10 · TaskCompletionButton */}
        <Section id="completion" title="Task Completion Button"
                  blurb="Bottom-of-screen CTA. 56 px minimum height. Label pulled from voice snippet.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Tile label="Success · Cleaning Complete" testid="tile-cta-success">
              <div className="w-full max-w-sm">
                <TaskCompletionButton voiceKey="VOICE_TASK_COMPLETED"
                                       icon={CheckCircle2} tone="success"
                                       testid="cta-success" />
              </div>
            </Tile>
            <Tile label="Primary · Start" testid="tile-cta-primary">
              <div className="w-full max-w-sm">
                <TaskCompletionButton voiceKey="VOICE_START_TASK"
                                       icon={Play} tone="primary"
                                       testid="cta-primary" />
              </div>
            </Tile>
            <Tile label="Loading" testid="tile-cta-loading">
              <div className="w-full max-w-sm">
                <TaskCompletionButton voiceKey="VOICE_FINISH_TASK"
                                       loading testid="cta-loading" />
              </div>
            </Tile>
            <Tile label="Danger · disabled" testid="tile-cta-disabled">
              <div className="w-full max-w-sm">
                <TaskCompletionButton voiceKey="VOICE_ERROR" tone="danger"
                                       disabled testid="cta-disabled" />
              </div>
            </Tile>
          </div>
        </Section>

        {/* 11 · AttentionOverlay */}
        <Section id="attention" title="Attention Overlay"
                  blurb="Full-screen emergency modal. Vibration + auto voice.">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <button type="button"
              className="rounded-2xl border-2 border-red-500 bg-red-50 text-red-800 px-4 py-3 font-bold hover:bg-red-100"
              onClick={() => openAttention("emergency")}
              data-testid="open-attention-emergency">
              <AlertTriangle className="inline w-5 h-5 mr-2" /> Emergency
            </button>
            <button type="button"
              className="rounded-2xl border-2 border-amber-500 bg-amber-50 text-amber-800 px-4 py-3 font-bold hover:bg-amber-100"
              onClick={() => openAttention("vip")}
              data-testid="open-attention-vip">
              <Crown className="inline w-5 h-5 mr-2" /> VIP
            </button>
            <button type="button"
              className="rounded-2xl border-2 border-slate-700 bg-slate-100 text-slate-800 px-4 py-3 font-bold hover:bg-slate-200"
              onClick={() => openAttention("override")}
              data-testid="open-attention-override">
              Override
            </button>
            <button type="button"
              className="rounded-2xl border-2 border-black bg-hocp-mint text-hocp-deepTeal px-4 py-3 font-bold hover:bg-hocp-mintStrong"
              onClick={() => openAttention("critical")}
              data-testid="open-attention-critical">
              Critical
            </button>
          </div>
        </Section>

        {/* HK-OS Demo Flow */}
        <Section id="demo-flow" title="HK-OS Demo Flow"
                  blurb="Illustrative composition: Next Task → Instruction → Scan → Progress → Complete → next task. No operational logic; UI only.">
          <DemoFlow />
        </Section>
      </main>

      {/* Attention overlays */}
      {attentionVariant === "emergency" && (
        <AttentionOverlay
          open variant="emergency"
          headline="305" subline="Emergency Task"
          voiceKey="VOICE_SUPERVISOR_REQUIRED"
          ctaLabelKey="VOICE_START_TASK"
          onDismiss={closeAttention}
          testid="attention-emergency"
        />
      )}
      {attentionVariant === "vip" && (
        <AttentionOverlay
          open variant="vip"
          headline="812" subline="VIP Guest"
          voiceKey="VOICE_VIP"
          ctaLabelKey="VOICE_ROOM_READY"
          onDismiss={closeAttention}
          testid="attention-vip"
        />
      )}
      {attentionVariant === "override" && (
        <AttentionOverlay
          open variant="override"
          headline="112" subline="Supervisor Override"
          voiceKey="VOICE_SUPERVISOR_REQUIRED"
          ctaLabelKey="VOICE_START_TASK"
          onDismiss={closeAttention}
          testid="attention-override"
        />
      )}
      {attentionVariant === "critical" && (
        <AttentionOverlay
          open variant="critical"
          headline="—" subline="Critical Alert"
          voiceKey="VOICE_ERROR"
          ctaLabelKey="VOICE_RETRY"
          onDismiss={closeAttention}
          testid="attention-critical"
        />
      )}
    </div>
  );
}

/** Demo composition — pure UI, no operational state machine. */
function DemoFlow() {
  const [step, setStep] = useState(0);
  const roomQueue = ["305", "204", "812"];
  const room = roomQueue[step % roomQueue.length];

  const steps = [
    { key: "VOICE_SCAN_ROOM", label: "Scan" },
    { key: "VOICE_START_TASK", label: "Start" },
    { key: "VOICE_PHOTO_REQUIRED", label: "Photo" },
    { key: "VOICE_RECORD_NOTE", label: "Note" },
    { key: "VOICE_FINISH_TASK", label: "Finish" },
  ];
  const currentStep = steps[step] || steps[0];

  return (
    <div className="rounded-3xl border-2 border-slate-200 bg-white overflow-hidden">
      <VoiceInstructionBar
        voiceKey={currentStep.key}
        progress={step / (steps.length - 1)}
        variant="hero"
        autoPlay={false}
        testid="demo-instruction"
      />
      <div className="p-6 grid gap-4 lg:grid-cols-[1fr_320px]">
        <NextTaskCard
          roomNumber={room}
          floorLabel="Floor 3 · Wing B"
          taskTypeKey="VOICE_ROOM_DIRTY"
          badges={[{ label: "VIP", tone: "vip" }]}
          onStart={() => setStep((n) => (n + 1) % steps.length)}
          testid="demo-next-task"
        />
        <div className="space-y-3">
          <ScanRoomButton onLaunch={() => setStep(1)} testid="demo-scan" />
          <VoiceRecorderButton promptKey="VOICE_RECORD_NOTE"
                                 testid="demo-recorder" />
          <TaskCompletionButton
            voiceKey="VOICE_TASK_COMPLETED"
            icon={CheckCircle2} tone="success"
            onClick={() => setStep((n) => (n + 1) % roomQueue.length)}
            testid="demo-complete"
          />
        </div>
      </div>
    </div>
  );
}

export default function HkOsGallery() {
  const token = useLocalAuthToken();
  return (
    <VoiceRegistryProvider locale="bn-IN" authToken={token} autoWarmup>
      <GalleryBody />
    </VoiceRegistryProvider>
  );
}
